/*
 * collection-scroll-memory.js
 *
 * Retomada de navegação em coleções grandes com infinite scroll.
 *
 * Problema: o infinite scroll acumula N páginas via ajax sem nunca alterar a URL.
 * Quando o usuário entra na PDP e volta, o iOS/Safari frequentemente descarta o
 * bfcache (pressão de memória — provável com 2k produtos) e recarrega o documento
 * original, ou seja, a página 1 no topo. Nenhuma gravação de scrollY resolve isso,
 * porque o documento restaurado simplesmente não tem os itens que existiam antes.
 *
 * Solução: a URL passa a refletir a página paginada em que o usuário está
 * (history.replaceState -> ?page=N). Assim, se o documento for descartado, o próprio
 * servidor devolve a fatia certa da coleção (uma página, ~24 itens) e só precisamos
 * reposicionar o scroll no item ancorado. O custo de retomada é O(1): independe de
 * quantos itens o usuário já tinha visto (1 request, 1 página), em qualquer device.
 *
 * Como o usuário pode cair no meio da coleção, também existe carregamento
 * ascendente (páginas anteriores) com compensação de scroll.
 */
(function () {
    'use strict';

    var grid = document.querySelector('#CollectionProductGrid ul.productListing[data-collection-grid]');
    if (!grid) return;

    var STORAGE_KEY = 'lolia:collection-position';

    // Registro separado, gravado só no clique num produto e nunca por scroll/flush: é o
    // que a PDP lê para continuar a coleção a partir do produto clicado (ver
    // product-collection-feed.js). STORAGE_KEY não serve para isso porque é reescrito a
    // cada scroll e, principalmente, no pagehide da própria navegação — o que trocaria o
    // produto clicado pelo item que estava no topo do viewport.
    var HANDOFF_KEY = 'lolia:collection-handoff';
    var MAX_AGE = 30 * 60 * 1000;
    var ITEM_SELECTOR = 'li.product[data-position]';

    var section = grid.closest('[data-section-id]');
    var sectionId = section ? section.getAttribute('data-section-id') : null;
    var pageSize = parseInt(grid.getAttribute('data-page-size'), 10) || 0;
    var totalItems = parseInt(grid.getAttribute('data-total-items'), 10) || 0;
    var totalPages = parseInt(grid.getAttribute('data-total-pages'), 10) || 1;
    var previousLabel = grid.getAttribute('data-load-previous-label') || 'Ver produtos anteriores';

    var urlPage = readPageFromUrl();
    var minPage = parseInt(grid.getAttribute('data-current-page'), 10) || urlPage || 1;
    var trackedPage = minPage;
    var loadedPages = {};
    loadedPages[minPage] = true;

    var baseKey = buildBaseKey();
    var busy = false;
    var sectionRenderWorks = !!sectionId;
    var upSentinel = null;
    var saveTimer = null;
    var rafScheduled = false;

    // Janela de DOM. O que estoura a memória do Safari no iOS não é uma página, é o
    // acúmulo: cada página são 30 cards de ~29 KB e ~130 tags (medido em
    // /collections/pecas-com-estoque?page=5), e no modo infinite elas entram sozinhas
    // enquanto o usuário rola. Mantemos vivas só as linhas ao redor do viewport; o
    // resto vira um espaçador de altura idêntica e volta ao se aproximar.
    //
    // Os limites são justos porque o custo por card vivo é alto: além das ~130 tags,
    // cada card carrega um slider slick (ver releaseCards). O crash relatado no iOS
    // acontecia com ~180 cards vivos, então o teto aqui fica bem abaixo disso.
    var KEEP_BEHIND_ITEMS = 50;
    var KEEP_AHEAD_ITEMS = 80;
    var MIN_ITEMS_TO_COLLAPSE = 120;

    // Piso em pixels para a fronteira de colapso, além do piso em itens acima.
    // Precisa ser maior que o rootMargin de reidratação (REHYDRATE_MARGIN_PX): um
    // espaçador que nascesse dentro daquela margem seria reidratado na hora e
    // colapsado de novo em seguida, num ciclo de fetch infinito. Contar itens não
    // garante isso sozinho, porque a altura do card muda com layout e device.
    var REHYDRATE_MARGIN_PX = 2500;
    var SAFE_COLLAPSE_PX = 3200;
    var maxPage = minPage;
    var spacerObserver = null;
    var spacerBanners = new WeakMap();
    var enforceTimer = null;
    var mutating = false;

    // Offset lido por theme.js (ajaxInfiniteScrollingMapData) para que o contador
    // "X de Y" continue correto quando a primeira página do DOM não é a página 1.
    window.collectionRenderedOffset = (minPage - 1) * pageSize;

    /* -------------------------------------------------- helpers de URL/estado */

    function readPageFromUrl() {
        var value = new URL(window.location.href).searchParams.get('page');
        var page = parseInt(value, 10);
        return page > 0 ? page : 1;
    }

    function buildBaseKey() {
        var u = new URL(window.location.href);
        u.searchParams.delete('page');
        u.searchParams.sort();
        return u.pathname + u.search;
    }

    function pageUrl(page, withSection) {
        var u = new URL(window.location.href);
        u.searchParams.set('page', page);
        if (withSection && sectionId) {
            u.searchParams.set('section_id', sectionId);
        } else {
            u.searchParams.delete('section_id');
        }
        return u.pathname + u.search;
    }

    function syncUrl(page) {
        if (page === urlPage || !window.history || !history.replaceState) return;

        var u = new URL(window.location.href);
        if (page > 1) {
            u.searchParams.set('page', page);
        } else {
            u.searchParams.delete('page');
        }

        try {
            history.replaceState(history.state, '', u.pathname + u.search + u.hash);
            urlPage = page;
        } catch (e) {
            /* noop */
        }
    }

    function readState() {
        try {
            var raw = window.sessionStorage.getItem(STORAGE_KEY);
            if (!raw) return null;

            var state = JSON.parse(raw);
            if (!state || state.key !== baseKey) return null;
            if ((Date.now() - state.at) > MAX_AGE) return null;

            return state;
        } catch (e) {
            return null;
        }
    }

    function writeState(item) {
        if (!item) return;

        var state = {
            key: baseKey,
            at: Date.now(),
            position: item.getAttribute('data-position'),
            handle: item.getAttribute('data-product-handle'),
            page: parseInt(item.getAttribute('data-page'), 10) || trackedPage,
            offset: Math.round(item.getBoundingClientRect().top)
        };

        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            /* modo privado / quota: a URL com ?page=N ainda garante a retomada aproximada */
        }
    }

    // Handoff para a PDP: posição exata do produto clicado, com a base da listagem
    // (ordenação/filtros inclusos) para o feed reproduzir a mesma ordem.
    function writeHandoff(item) {
        if (!item) return;

        try {
            window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({
                key: baseKey,
                at: Date.now(),
                position: item.getAttribute('data-position'),
                handle: item.getAttribute('data-product-handle')
            }));
        } catch (e) {
            /* modo privado / quota: a PDP cai na coleção de fallback da seção */
        }
    }

    /* ------------------------------------------------- localização do usuário */

    // Descobre o item visível mais alto sem varrer o DOM: uma sequência curta de
    // hit-tests é O(1) e não força layout de milhares de nós.
    function visibleItem() {
        var height = window.innerHeight || 0;
        var width = window.innerWidth || 0;
        var ys = [0.02, 0.12, 0.25, 0.45, 0.6];
        var xs = [0.5, 0.25, 0.75];

        for (var i = 0; i < ys.length; i++) {
            for (var j = 0; j < xs.length; j++) {
                var el = document.elementFromPoint(Math.round(width * xs[j]), Math.round(height * ys[i]));
                var item = el && el.closest ? el.closest(ITEM_SELECTOR) : null;
                if (item && grid.contains(item)) return item;
            }
        }

        return null;
    }

    function track() {
        var item = visibleItem();
        if (!item) return;

        var page = parseInt(item.getAttribute('data-page'), 10);
        if (page > 0 && page !== trackedPage) {
            trackedPage = page;
            syncUrl(page);
        }

        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(function () {
            saveTimer = null;
            writeState(visibleItem());
        }, 250);
    }

    window.addEventListener('scroll', function () {
        if (rafScheduled) return;
        rafScheduled = true;
        requestAnimationFrame(function () {
            rafScheduled = false;
            track();
        });
        scheduleEnforce();
    }, { passive: true });

    // Clique em um produto: âncora exata, sem depender do hit-test.
    grid.addEventListener('click', function (event) {
        var target = event.target;
        var link = target && target.closest ? target.closest('a[href]') : null;
        if (!link) return;

        var item = link.closest(ITEM_SELECTOR);
        if (!item) return;

        var page = parseInt(item.getAttribute('data-page'), 10);
        if (page > 0) {
            trackedPage = page;
            syncUrl(page);
        }

        if (saveTimer) {
            clearTimeout(saveTimer);
            saveTimer = null;
        }
        writeState(item);
        writeHandoff(item);
    }, true);

    function flush() {
        if (saveTimer) {
            clearTimeout(saveTimer);
            saveTimer = null;
        }
        writeState(visibleItem());
    }

    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') flush();
    });

    /* ---------------------------------------------------------- reposicionamento */

    function itemByPosition(position) {
        var exact = grid.querySelector('li.product[data-position="' + String(position).replace(/"/g, '') + '"]');
        if (exact) return exact;

        var target = parseInt(position, 10);
        if (isNaN(target)) return null;

        var items = grid.querySelectorAll(ITEM_SELECTOR);
        var best = null;
        var bestDiff = Infinity;

        for (var i = 0; i < items.length; i++) {
            var diff = Math.abs(parseInt(items[i].getAttribute('data-position'), 10) - target);
            if (diff < bestDiff) {
                bestDiff = diff;
                best = items[i];
            }
        }

        return best;
    }

    // Mantém o item ancorado no mesmo ponto do viewport enquanto imagens lazy
    // terminam de reservar altura. Aborta no primeiro gesto do usuário.
    function hold(item, offset, duration) {
        var until = Date.now() + duration;
        var aborted = false;
        var abort = function () { aborted = true; };
        var events = ['touchstart', 'wheel', 'keydown', 'pointerdown'];

        events.forEach(function (name) {
            window.addEventListener(name, abort, { passive: true, once: true });
        });

        (function tick() {
            if (aborted || Date.now() > until || !item.isConnected) {
                events.forEach(function (name) {
                    window.removeEventListener(name, abort);
                });
                return;
            }

            var delta = Math.round(item.getBoundingClientRect().top) - offset;
            if (Math.abs(delta) > 2) window.scrollBy(0, delta);

            requestAnimationFrame(tick);
        })();
    }

    function restore(state) {
        var item = state.handle
            ? grid.querySelector('li.product[data-product-handle="' + state.handle.replace(/"/g, '') + '"]')
            : null;

        if (!item) item = itemByPosition(state.position);
        if (!item) return;

        var offset = typeof state.offset === 'number' ? state.offset : 0;
        var y = item.getBoundingClientRect().top + window.pageYOffset - offset;

        window.scrollTo(0, Math.max(0, y));
        hold(item, offset, 1500);
    }

    /* -------------------------------------------------- carregamento ascendente */

    function fetchItems(page) {
        var request = function (withSection) {
            return fetch(pageUrl(page, withSection), {
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            }).then(function (response) {
                if (!response.ok) throw new Error('HTTP ' + response.status);
                return response.text();
            }).then(function (html) {
                var doc = new DOMParser().parseFromString(html, 'text/html');
                return Array.prototype.slice.call(
                    doc.querySelectorAll('ul.productListing > ' + ITEM_SELECTOR)
                );
            });
        };

        if (!sectionRenderWorks) return request(false);

        // Render por section_id devolve só a seção (bem mais leve). Se o tema não
        // suportar nesse contexto, cai para o documento completo uma única vez.
        return request(true).then(function (items) {
            if (items.length) return items;
            sectionRenderWorks = false;
            return request(false);
        }).catch(function () {
            sectionRenderWorks = false;
            return request(false);
        });
    }

    function insertSorted(items) {
        // Um espaçador quase nunca cobre páginas inteiras — ele nasce alinhado a linhas.
        // Ao reidratar, parte dos itens daquela página ainda está viva, então inserir
        // tudo duplicaria produtos. Deduplica por posição.
        var existing = {};
        var current = grid.querySelectorAll(ITEM_SELECTOR);
        for (var i = 0; i < current.length; i++) {
            existing[current[i].getAttribute('data-position')] = true;
        }

        var fresh = items.filter(function (item) {
            return !existing[item.getAttribute('data-position')];
        });
        if (!fresh.length) return;

        var fragment = document.createDocumentFragment();
        fresh.forEach(function (item) {
            fragment.appendChild(document.importNode(item, true));
        });

        var firstPosition = parseInt(fresh[0].getAttribute('data-position'), 10);
        var existing = grid.querySelectorAll(ITEM_SELECTOR);
        var reference = null;

        for (var i = 0; i < existing.length; i++) {
            if (parseInt(existing[i].getAttribute('data-position'), 10) > firstPosition) {
                reference = existing[i];
                break;
            }
        }

        grid.insertBefore(fragment, reference);
    }

    function updateCounter() {
        window.collectionRenderedOffset = (minPage - 1) * pageSize;

        var counter = document.querySelector('#CollectionProductGrid [data-total-end]');
        if (!counter) return;

        // Contar os itens do DOM deixou de servir: com a janela de DOM ativa eles
        // somem e voltam. O progresso é a página mais alta já carregada.
        var items = grid.querySelectorAll(ITEM_SELECTOR);
        for (var i = 0; i < items.length; i++) {
            var page = parseInt(items[i].getAttribute('data-page'), 10);
            if (page > maxPage) maxPage = page;
        }

        var rendered = maxPage * pageSize;
        if (totalItems > 0) rendered = Math.min(rendered, totalItems);

        counter.textContent = rendered + ' ';

        var bar = document.querySelector('.pagination-total-progress .pagination-total-item');
        if (bar && totalItems > 0) {
            bar.style.width = Math.min(100, Math.round(rendered * 100 / totalItems)) + '%';
        }
    }

    function afterInsert() {
        try {
            if (window.wishlist && window.wishlist.show && window.sharedFunctions) {
                window.sharedFunctions.setLocalStorageProductForWishlist();
            }

            if (window.sharedFunctions && window.sharedFunctions.checkNeedToConvertCurrency
                && window.sharedFunctions.checkNeedToConvertCurrency() && window.Currency) {
                var active = document.querySelector('#currencies .active');
                window.Currency.convertAll(
                    window.shop_currency,
                    active ? active.getAttribute('data-currency') : window.shop_currency,
                    'span.money',
                    'money_format'
                );
            }

            if (typeof window.updateSlideGridProduct === 'function') window.updateSlideGridProduct();
            if (typeof window.resizeAllGridItems === 'function' && document.querySelector('.collection-masonry')) {
                window.resizeAllGridItems();
            }
        } catch (e) {
            /* integrações opcionais não devem quebrar a navegação */
        }
    }

    /* -------------------------------------------------------- janela de DOM */

    // Agrupa por offsetTop: com `order: var(--position-grid)` a ordem visual não é a
    // ordem do DOM, e só o layout diz quem está na mesma linha. Colapsar linhas
    // inteiras é o que permite trocar o lote por um espaçador de largura total sem
    // mudar a quebra do flex — colapsar "meia linha" reorganizaria o grid todo.
    function visualRows() {
        var children = grid.children;
        var byTop = {};
        var tops = [];

        for (var i = 0; i < children.length; i++) {
            var el = children[i];
            if (el.tagName !== 'LI') continue;
            if (el.classList.contains('collection-batch-spacer')) continue;

            var top = Math.round(el.offsetTop);
            if (!byTop[top]) {
                byTop[top] = [];
                tops.push(top);
            }
            byTop[top].push(el);
        }

        tops.sort(function (a, b) { return a - b; });

        return tops.map(function (top) {
            return { top: top, items: byTop[top] };
        });
    }

    // Tirar o <li> do DOM com Node.remove() não devolve memória nenhuma.
    //
    // updateSlideGridProduct() inicializa um slick em cada .card-media do grid, e o
    // slick registra por instância três handlers em window (resize, orientationchange,
    // load) e um em document, via $.proxy(fn, instância). Esses handlers só saem no
    // destroy. Enquanto estiverem lá, eles seguram a instância, que segura $slider,
    // $slides, $slideTrack, $list, $dots, as setas e os clones de slide — ou seja, o
    // card inteiro e todas as <img> dele continuam alcançáveis a partir de window
    // mesmo depois de o <li> sair da página. Era por isso que a janela de DOM não
    // reduzia o consumo e o Safari no iOS continuava derrubando a aba.
    //
    // Então: destaca do documento (para o destroy não custar layout), desmonta o
    // slick e só aí remove pelo jQuery, que é quem limpa data/handlers da subárvore.
    function releaseCards(elements) {
        var $ = window.jQuery;

        for (var i = 0; i < elements.length; i++) {
            var el = elements[i];

            if (!$) {
                el.remove();
                continue;
            }

            var $el = $(el);
            $el.detach();

            try {
                $el.find('.card-media.slick-initialized').slick('unslick');
            } catch (e) {
                /* slider já desmontado ou slick ausente: seguir com a limpeza */
            }

            $el.remove();
        }
    }

    function rowItemCount(row) {
        var n = 0;
        for (var i = 0; i < row.items.length; i++) {
            if (row.items[i].hasAttribute('data-position')) n++;
        }
        return n;
    }

    function collapseRows(rows, from, to, nextTop) {
        var top = rows[from].top;
        var bottom = nextTop;

        if (bottom == null) {
            bottom = top;
            for (var r = from; r <= to; r++) {
                rows[r].items.forEach(function (el) {
                    var b = el.offsetTop + el.offsetHeight;
                    if (b > bottom) bottom = b;
                });
            }
        }

        var height = Math.round(bottom - top);
        if (height <= 0) return;

        var pages = {};
        var banners = [];
        var firstPosition = Infinity;
        var removed = [];

        for (var i = from; i <= to; i++) {
            rows[i].items.forEach(function (el) {
                var position = parseInt(el.getAttribute('data-position'), 10);

                if (isNaN(position)) {
                    // Banners de coleção vêm de um metafield injetado por script inline,
                    // não de ?page=N: guardamos o HTML para devolver na reidratação.
                    banners.push(el.outerHTML);
                } else {
                    if (position < firstPosition) firstPosition = position;
                    var page = parseInt(el.getAttribute('data-page'), 10);
                    if (page > 0) pages[page] = true;
                }

                removed.push(el);
            });
        }

        var pageList = Object.keys(pages);
        if (!pageList.length) return;
        if (!isFinite(firstPosition)) firstPosition = 0;

        var spacer = document.createElement('li');
        spacer.className = 'collection-batch-spacer';
        spacer.setAttribute('data-spacer-pages', pageList.join(','));
        spacer.setAttribute('aria-hidden', 'true');
        spacer.style.cssText = 'flex:0 0 100%;width:100%;max-width:100%;list-style:none;margin:0;padding:0;'
            + 'height:' + height + 'px;order:' + firstPosition + ';';

        if (banners.length) spacerBanners.set(spacer, banners);

        grid.insertBefore(spacer, removed[0]);
        releaseCards(removed);

        if (spacerObserver) spacerObserver.observe(spacer);
    }

    function enforceWindow() {
        if (busy || mutating) return;
        if (grid.querySelectorAll(ITEM_SELECTOR).length < MIN_ITEMS_TO_COLLAPSE) return;

        var anchor = visibleItem();
        if (!anchor) return;

        var rows = visualRows();
        if (rows.length < 6) return;

        var anchorTop = Math.round(anchor.offsetTop);
        var anchorRow = -1;
        for (var i = 0; i < rows.length; i++) {
            if (rows[i].top === anchorTop) { anchorRow = i; break; }
        }
        if (anchorRow < 0) return;

        var count = 0;
        var keepFrom = anchorRow;
        while (keepFrom > 0 && count < KEEP_BEHIND_ITEMS) {
            keepFrom--;
            count += rowItemCount(rows[keepFrom]);
        }

        count = 0;
        var keepTo = anchorRow;
        while (keepTo < rows.length - 1 && count < KEEP_AHEAD_ITEMS) {
            keepTo++;
            count += rowItemCount(rows[keepTo]);
        }

        // Empurra as fronteiras para fora da margem de reidratação. A janela efetiva
        // é o maior entre o piso em itens e o piso em pixels, então os contadores
        // acima podem ser justos sem risco de ciclo colapsa/reidrata.
        while (keepFrom > 0 && rows[keepFrom].top > anchorTop - SAFE_COLLAPSE_PX) keepFrom--;
        while (keepTo < rows.length - 1 && rows[keepTo].top < anchorTop + SAFE_COLLAPSE_PX) keepTo++;

        mutating = true;

        // Abaixo primeiro: não mexe no scroll atual.
        if (keepTo < rows.length - 1) collapseRows(rows, keepTo + 1, rows.length - 1, null);

        // Acima: o espaçador tem a altura exata do que saiu, então a posição não muda.
        // Ainda assim confere pela âncora, para absorver arredondamento.
        if (keepFrom > 0) {
            var before = Math.round(anchor.getBoundingClientRect().top);
            collapseRows(rows, 0, keepFrom - 1, rows[keepFrom].top);
            var after = Math.round(anchor.getBoundingClientRect().top);
            if (after !== before) window.scrollBy(0, after - before);
        }

        mutating = false;
        updateCounter();
    }

    function rehydrateSpacer(spacer) {
        if (busy || !spacer.isConnected) return;

        var pages = (spacer.getAttribute('data-spacer-pages') || '')
            .split(',')
            .map(Number)
            .filter(function (p) { return p > 0; });

        if (!pages.length) {
            spacer.remove();
            return;
        }

        busy = true;

        var anchor = visibleItem();
        var before = anchor ? Math.round(anchor.getBoundingClientRect().top) : null;
        var heightBefore = document.documentElement.scrollHeight;

        Promise.all(pages.map(function (page) { return fetchItems(page); })).then(function (groups) {
            if (!spacer.isConnected) return;

            mutating = true;

            var banners = spacerBanners.get(spacer);
            if (banners && banners.length) {
                var holder = document.createElement('ul');
                holder.innerHTML = banners.join('');
                while (holder.firstElementChild) grid.appendChild(holder.firstElementChild);
            }

            spacer.remove();
            groups.forEach(function (items) {
                if (items && items.length) insertSorted(items);
            });

            mutating = false;
            compensate(anchor, before, heightBefore);
            updateCounter();
            afterInsert();
        }).catch(function () {
            /* o espaçador continua no lugar: tenta de novo na próxima aproximação */
        }).then(function () {
            busy = false;
        });
    }

    function compensate(anchor, before, heightBefore) {
        if (anchor && before !== null && anchor.isConnected) {
            var after = Math.round(anchor.getBoundingClientRect().top);
            if (after !== before) window.scrollBy(0, after - before);
            hold(anchor, before, 800);
            return;
        }

        var grew = document.documentElement.scrollHeight - heightBefore;
        if (grew !== 0) window.scrollBy(0, grew);
    }

    function setupWindowing() {
        if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;

        spacerObserver = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
                if (entries[i].isIntersecting) {
                    spacerObserver.unobserve(entries[i].target);
                    rehydrateSpacer(entries[i].target);
                    return;
                }
            }
            // Margem larga de propósito: subindo rápido, 1200px chega tarde e o usuário
            // enxerga o vão do espaçador. Quem garante que nada é reidratado só para
            // ser colapsado de novo é SAFE_COLLAPSE_PX, que mantém a fronteira de
            // colapso fora desta margem — por isso as duas saem da mesma constante.
        }, { rootMargin: REHYDRATE_MARGIN_PX + 'px 0px' });

        // Pega tanto o append do infinite scroll quanto o prepend daqui.
        new MutationObserver(function () {
            if (mutating) return;
            scheduleEnforce();
        }).observe(grid, { childList: true });
    }

    function scheduleEnforce() {
        if (enforceTimer) clearTimeout(enforceTimer);
        // Só em repouso: remover nós no meio de um fling causaria travada visível.
        enforceTimer = setTimeout(function () {
            enforceTimer = null;
            enforceWindow();
        }, 350);
    }

    function loadPrevious() {
        if (busy || minPage <= 1) return;

        var page = minPage - 1;
        if (loadedPages[page]) {
            minPage = page;
            refreshUpwardLoader();
            return;
        }

        busy = true;
        setLoading(true);

        fetchItems(page).then(function (items) {
            loadedPages[page] = true;
            minPage = page;

            if (items.length) {
                var anchor = visibleItem();
                var before = anchor ? Math.round(anchor.getBoundingClientRect().top) : null;
                var heightBefore = document.documentElement.scrollHeight;

                mutating = true;
                insertSorted(items);
                mutating = false;

                // Sem âncora (hit-test caiu num banner ou num vão do grid) a compensação
                // cai no delta de altura. Nunca deixar de compensar: o conteúdo entra
                // acima do viewport e o usuário seria jogado para outro ponto.
                compensate(anchor, before, heightBefore);
                updateCounter();
                afterInsert();
            }
        }).catch(function () {
            /* mantém o botão para nova tentativa manual */
        }).then(function () {
            busy = false;
            setLoading(false);
            refreshUpwardLoader();
        });
    }

    function setLoading(loading) {
        if (!upSentinel) return;
        upSentinel.classList.toggle('is-loading', !!loading);
        var button = upSentinel.querySelector('button');
        if (button) button.disabled = !!loading;
    }

    function refreshUpwardLoader() {
        if (minPage <= 1) {
            teardownUpwardLoader();
            return;
        }

        if (upSentinel) return;

        upSentinel = document.createElement('div');
        upSentinel.className = 'collection-load-previous';
        upSentinel.innerHTML = '<button type="button" class="button button--secondary">'
            + previousLabel + '</button>';
        grid.parentNode.insertBefore(upSentinel, grid);

        // Sob toque, uma página por toque. Nada de IntersectionObserver aqui: o
        // sentinel vive acima do grid, então um observer só para de disparar se a
        // compensação de scroll funcionar — e um único caso sem compensação viraria
        // um cascade que prepende a coleção inteira e derruba o Safari por memória.
        upSentinel.querySelector('button').addEventListener('click', loadPrevious);
    }

    function teardownUpwardLoader() {
        if (upSentinel) {
            upSentinel.remove();
            upSentinel = null;
        }
    }

    /* ------------------------------------------------------------------ start */

    // Ordenação/itens por página do toolbar reaproveitam a query da URL: não deve
    // herdar o ?page=N que passamos a escrever.
    if (window.Shopify && window.Shopify.queryParams) delete window.Shopify.queryParams.page;

    var saved = readState();
    var navigation = performance.getEntriesByType
        ? performance.getEntriesByType('navigation')[0]
        : null;
    var cameBack = navigation ? navigation.type === 'back_forward' : false;

    // O botão de páginas anteriores entra antes do reposicionamento: inserir depois
    // deslocaria a lista e o item ancorado sairia do lugar.
    refreshUpwardLoader();
    setupWindowing();

    // Em restauração via bfcache o script não roda de novo (documento vivo), então
    // não há conflito com o scroll que o próprio navegador restaura.
    if (saved && (cameBack || urlPage > 1)) restore(saved);

    track();
})();
